import { useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';

import { Typography, Box } from '@mui/material';

// icons
import { ReactComponent as CheckedIcon } from '../../assets/icons/status-checked-icon.svg';
import { ReactComponent as WarningIcon } from '../../assets/icons/status-yellow-icon.svg';
import { ReactComponent as MenuIcon } from '../../assets/icons/menu-arrow-icon.svg';

// constants
import { MOMENT_DATE_FORMAT, MOMENT_DISPLAY_FORMAT, MOMENT_TIME_DISPLAY_FORMAT } from '../../constants/date_format';
import { colors } from '../../theme';

interface StatusProps {
    isAllCurrent: boolean;
    isLastUpdatedAt: any;
    dataSourceStatus: any;
}

const DateStampBox = (props: any) => {
    const { dataSourceStatus } = props;

    const [isShowDates, setIsShowDates] = useState(false);

    const handleShowDates = () => {
        setIsShowDates((state) => !state);
    };

    const renderRefreshTime = (date: any) => {
        if (!date) return;
        if (moment().format(MOMENT_DATE_FORMAT) === moment(date).format(MOMENT_DATE_FORMAT)) {
            return `Today, ${moment(date).format(MOMENT_TIME_DISPLAY_FORMAT)}`;
        } else return moment(date).format(MOMENT_DISPLAY_FORMAT);
    };

    return (
        <Contanier onMouseOver={handleShowDates} onMouseOut={handleShowDates}>
            <DataStatus isAllCurrent={dataSourceStatus?.isAllCurrent} isLastUpdatedAt={dataSourceStatus?.lastUpdatedAt} dataSourceStatus={dataSourceStatus} />
            {!dataSourceStatus ? (
                <span>Loading...</span>
            ) : dataSourceStatus.lastUpdatedAt && dataSourceStatus.isAllCurrent ? (
                <>
                    <span>Updated</span>
                    <Typography>{renderRefreshTime(dataSourceStatus.lastUpdatedAt)}</Typography>
                </>
            ) : (
                <span>Partly Updated</span>
            )}
            <MenuIcon style={{ marginLeft: '4px' }} />
            {isShowDates && (
                <InfoContainer>
                    <Header>
                        <Typography variant="h5">Data Update Status</Typography>
                        <IndexBox>
                            <FlexBox sx={{ mr: '4px' }}>
                                <CheckedIcon />
                                <Typography variant="body2">Latest Update</Typography>
                            </FlexBox>
                            <FlexBox>
                                <WarningIcon />
                                <Typography variant="body2">Update Failed</Typography>
                            </FlexBox>
                        </IndexBox>
                    </Header>

                    {dataSourceStatus ? (
                        dataSourceStatus.dataSourceStatuses.map((status: any, index: any) => {
                            return (
                                <>
                                    <StampLabel key={index}>
                                        <Typography variant="h5">{status.name}</Typography>
                                        <FlexBox>
                                            <Typography variant="body2" sx={{ mr: '8px' }}>
                                                {renderRefreshTime(status.lastRefreshTime)}
                                            </Typography>
                                            {status.isCurrent ? <CheckedIcon /> : <WarningIcon />}
                                        </FlexBox>
                                    </StampLabel>
                                </>
                            );
                        })
                    ) : (
                        <Typography>No data to display.</Typography>
                    )}

                    {dataSourceStatus?.nextUpdateAt && (
                        <NextRefreshLabel>
                            <Typography variant="h5">Next Refresh</Typography>
                            <FlexBox>
                                <Typography variant="body2">{renderRefreshTime(dataSourceStatus.nextUpdateAt)}</Typography>
                            </FlexBox>
                        </NextRefreshLabel>
                    )}
                </InfoContainer>
            )}
        </Contanier>
    );
};

export default DateStampBox;

const Contanier = styled.div`
    display: flex;
    align-items: center;
    cursor: default;
    padding: 5px 8px;
    border-radius: 5px;

    span {
        font-weight: 500;
        margin-right: 5px;
    }

    &:hover {
        background-color: rgba(22, 47, 66, 0.5);
    }
`;

const DataStatus = styled.span<StatusProps>`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${(props) => (props.isAllCurrent ? colors.green.main : colors.red.main)};
    margin-right: 5px;

    ${(props) =>
        props.dataSourceStatus &&
        !props.isLastUpdatedAt && {
            backgroundColor: colors.red.main,
        }}
`;

const InfoContainer = styled.div`
    background-color: ${colors.white};
    min-width: 350px;
    padding: 12px;
    position: absolute;
    top: 95%;
    /* left: 0; */
    border-radius: 5px;
    box-shadow: ${colors.boxShadow};
`;

const Header = styled.div`
    margin-bottom: 8px;
    h5 {
        font-size: 14px;
        color: ${colors.navy.main};
        font-weight: 500;
    }
`;

const StampLabel = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid ${colors.bg};
    border-radius: 4px;
    padding: 8px;
    margin: 4px 0px;

    h5 {
        font-size: 14px;
        text-transform: capitalize;
        color: ${colors.black};
    }
    p {
        color: ${colors.grey.dark};
    }
`;

// Mirrors StampLabel, minus the status icon. The extra right margin on the
// timestamp reserves the 12px icon + 8px gap so it lines up with the rows above.
const NextRefreshLabel = styled(StampLabel)`
    p {
        margin-right: 20px;
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
    }
`;

const IndexBox = styled(Box)`
    && {
        display: inline-flex;
        align-items: center;
        border: 1px solid ${colors.grey.light};
        border-radius: 25px;
        padding: 2px 8px 2px 4px;
        margin-top: 4px;
        p {
            color: ${colors.black};
        }
        svg {
            margin: 0px 4px;
        }
    }
`;
